// --- Per-Provider Model Lists ---
/**
 * Single source of truth for the provider-type axis.
 *
 * Everything else on this axis is DERIVED from this tuple — the `AIProviderType`
 * union, the writable-provider list (`AI_PROVIDER_TYPES_WRITABLE`), and the
 * `getModelsForProvider` exhaustiveness pin. A second hand-written literal list
 * anywhere is how the schema enum silently drifted behind this union before
 * (`anthropic`/`echo` were reachable in code and rejected by the schema).
 */
export const AI_PROVIDER_TYPES = [
    'copilot',
    'claude-cli',
    'minimax',
    'openai',
    'custom',
    'opencode',
    'vertex',
    'anthropic',
    'nim',
    'echo',
];
/** Narrow a derived list to zod's non-empty tuple shape. Fails loud when empty. */
function asNonEmptyTuple(values) {
    if (values.length === 0) {
        throw new Error('AI provider type list must not be empty');
    }
    return values;
}
/**
 * Provider types a caller may write — DERIVED from AI_PROVIDER_TYPES by removing
 * the E2E stub.
 *
 * TWO consumers, and only one of them runs on a request:
 *   - aiProfile.schema.ts's zod enum — declared, NOT route-wired (#2581)
 *   - aiProfile.service's `assertProviderWritable` — the gate an HTTP write
 *     actually passes through
 * Both read this list, so they cannot disagree about what is writable.
 */
export const AI_PROVIDER_TYPES_WRITABLE = asNonEmptyTuple(AI_PROVIDER_TYPES.filter((t) => t !== 'echo'));
/** NVIDIA NIM deployment target — NVIDIA-hosted API vs. a self-hosted NIM container. */
export const NIM_DEPLOYMENTS = ['hosted', 'self-hosted'];
/** NVIDIA-hosted NIM endpoint. Fixed — never derived from a user-supplied host. */
export const NIM_HOSTED_BASE_URL = 'https://integrate.api.nvidia.com/v1';
/**
 * Self-hosted NIM containers ignore the Authorization value but the OpenAI SDK
 * throws when `apiKey` is undefined, so a placeholder is sent instead.
 */
export const NIM_SELF_HOSTED_PLACEHOLDER_KEY = 'not-used';
export const COPILOT_MODELS = [
    { value: '', label: 'Varsayılan (Copilot seçimi)', description: 'Copilot CLI varsayılan modeli kullanır' },
    { value: 'gpt-5.4', label: 'GPT-5.4', description: 'OpenAI — standard tier' },
    { value: 'gpt-5.3-codex', label: 'GPT-5.3 Codex', description: 'OpenAI — standard tier' },
    { value: 'gpt-5.2-codex', label: 'GPT-5.2 Codex', description: 'OpenAI — standard tier' },
    { value: 'gpt-5.2', label: 'GPT-5.2', description: 'OpenAI — standard tier' },
    { value: 'gpt-5.1', label: 'GPT-5.1', description: 'OpenAI — standard tier' },
    { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini', description: 'OpenAI — fast/cheap tier' },
    { value: 'gpt-5-mini', label: 'GPT-5 Mini', description: 'OpenAI — fast/cheap tier' },
    { value: 'gpt-4.1', label: 'GPT-4.1', description: 'OpenAI — fast/cheap tier' },
    { value: 'claude-opus-4.6', label: 'Claude Opus 4.6', description: 'Anthropic — premium tier' },
    { value: 'claude-opus-4.5', label: 'Claude Opus 4.5', description: 'Anthropic — premium tier' },
    { value: 'claude-sonnet-4.6', label: 'Claude Sonnet 4.6', description: 'Anthropic — standard tier' },
    { value: 'claude-sonnet-4.5', label: 'Claude Sonnet 4.5', description: 'Anthropic — standard tier' },
    { value: 'claude-sonnet-4', label: 'Claude Sonnet 4', description: 'Anthropic — standard tier' },
    { value: 'claude-haiku-4.5', label: 'Claude Haiku 4.5', description: 'Anthropic — fast/cheap tier' },
];
export const MINIMAX_MODELS = [
    { value: 'MiniMax-M3', label: 'MiniMax M3', description: 'Flagship — 1M context, multimodal (görsel/video), agentic coding, MSA sparse attention' },
    { value: 'MiniMax-M2.7', label: 'MiniMax M2.7', description: 'En güçlü — self-improvement, ~60 tps' },
    { value: 'MiniMax-M2.7-highspeed', label: 'MiniMax M2.7 Highspeed', description: 'M2.7 hızlı — ~100 tps (Token Plan gerektirir)' },
    { value: 'MiniMax-M2.5', label: 'MiniMax M2.5', description: 'Kod ve karmaşık görevler, ~60 tps' },
    { value: 'MiniMax-M2.5-highspeed', label: 'MiniMax M2.5 Highspeed', description: 'M2.5 hızlı — ~100 tps (Token Plan gerektirir)' },
    { value: 'MiniMax-M2.1', label: 'MiniMax M2.1', description: 'Çok dilli programlama, ~60 tps' },
    { value: 'MiniMax-M2.1-highspeed', label: 'MiniMax M2.1 Highspeed', description: 'M2.1 hızlı — ~100 tps (Token Plan gerektirir)' },
    { value: 'MiniMax-M2', label: 'MiniMax M2', description: 'Agent ve ileri muhakeme' },
];
export const OPENAI_MODELS = [
    { value: 'gpt-4.1', label: 'GPT-4.1', description: 'OpenAI — en gelişmiş' },
    { value: 'gpt-4o', label: 'GPT-4o', description: 'OpenAI — hızlı multimodal' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'OpenAI — hafif ve ekonomik' },
    { value: 'o3', label: 'o3', description: 'OpenAI — ileri düzey muhakeme' },
    { value: 'o4-mini', label: 'o4-mini', description: 'OpenAI — hızlı muhakeme' },
];
/**
 * `fetchedAt` of `__fixtures__/anthropic-models.json` — the `GET /v1/models`
 * snapshot `ANTHROPIC_MODELS` is pinned to. The parity test holds the two equal,
 * so re-capturing the snapshot forces this constant (and the editor's
 * "snapshot <date>" line) to move with it.
 */
export const ANTHROPIC_MODELS_SNAPSHOT_AT = '2026-09-02T05:54:26Z';
/**
 * Anthropic native API models (gh#2602).
 *
 * DASHED ids, as the API names them (`claude-opus-4-6`), never the dotted
 * Copilot CLI aliases (`claude-opus-4.6`) this provider used to borrow from
 * COPILOT_MODELS — a dotted id is a 404 on `/v1/messages`.
 *
 * Curation = ids listed by the `/v1/models` snapshot above ∩ Anthropic's current
 * model set. Two listed families are left out on purpose: the dated snapshot
 * ids (`claude-opus-4-5-20251101`, `claude-sonnet-4-5-20250929`,
 * `claude-haiku-4-5-20251001` — Haiku 4.5's alias is NOT listed, only its dated
 * id) and the access-gated `claude-mythos-*` line, which the snapshot does not
 * carry for this org. No `''` row: an empty model is not a choice here.
 */
export const ANTHROPIC_MODELS = [
    { value: 'claude-fable-5-1', label: 'Claude Fable 5.1', description: 'Anthropic — en yetenekli genel model, 1M context' },
    { value: 'claude-fable-5', label: 'Claude Fable 5', description: 'Anthropic — Fable ailesi, 1M context' },
    { value: 'claude-opus-5', label: 'Claude Opus 5', description: 'Anthropic — premium tier, 1M context' },
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8', description: 'Anthropic — premium tier' },
    { value: 'claude-opus-4-7', label: 'Claude Opus 4.7', description: 'Anthropic — premium tier' },
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6', description: 'Anthropic — premium tier' },
    { value: 'claude-sonnet-5', label: 'Claude Sonnet 5', description: 'Anthropic — standard tier, 1M context' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', description: 'Anthropic — standard tier' },
];
export const VERTEX_MODELS = [
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview', description: 'Google — premium, düşünme yetenekli (2026-02)' },
    { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite', description: 'Google — hızlı/ekonomik 3.1 ailesi' },
    { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash Preview', description: 'Google — genel 3 Flash' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', description: 'Google — derin düşünme ve kodlama (stable)' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', description: 'Google — fiyat/performans dengeli, düşünme destekli (stable)' },
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', description: 'Google — en hızlı ve ekonomik 2.5 (stable)' },
];
export const OPENCODE_MODELS = [
    { value: 'MiniMax-M2.7', label: 'MiniMax M2.7', description: 'OpenCode varsayılan — güçlü agent' },
    { value: 'big-pickle', label: 'Big Pickle', description: 'OpenCode — deneysel model' },
    { value: 'gpt-5-nano', label: 'GPT-5 Nano', description: 'OpenCode — hızlı kodlama' },
    { value: 'hy3-preview-free', label: 'Hy3 Preview', description: 'OpenCode — free tier' },
    { value: 'minimax-m2.5-free', label: 'MiniMax M2.5 Free', description: 'OpenCode — free tier' },
];
/**
 * NVIDIA NIM (hosted) chat models — curated subset of the live catalogue.
 *
 * Every id here is copied verbatim from a snapshot of
 * `GET https://integrate.api.nvidia.com/v1/models`, stored alongside this file
 * at `__fixtures__/nim-hosted-models.json`; the parity test asserts this list is
 * a subset of that snapshot so a retired id cannot sit here unnoticed. NVIDIA
 * answers a retired model with HTTP 410, not 404 — `meta/llama-3.3-70b-instruct`
 * is the worked example in docs/test-runbooks/nim-provider.md.
 *
 * Curation: chat/instruct models only. Embedding, guard/safety, reward,
 * translate and vision-only models are deliberately absent — they do not answer
 * `/chat/completions` in the shape this provider builds.
 *
 * `capabilities` is written ONLY from a live probe recorded in the runbook.
 * Absent flags mean unverified, NOT unsupported.
 */
export const NIM_MODELS = [
    {
        value: 'nvidia/nemotron-3-super-120b-a12b',
        label: 'Nemotron 3 Super 120B-A12B',
        description: 'NVIDIA — MoE, 120B total / 12B active; reasoning + tool calling',
        capabilities: { toolCalling: true, streamingToolCalls: true },
    },
    // `nvidia/nemotron-3-nano-30b-a3b` left the hosted catalogue between the
    // 2026-08-29 and 2026-09-02 snapshots and was removed here with it.
    {
        value: 'nvidia/nemotron-3-ultra-550b-a55b',
        label: 'Nemotron 3 Ultra 550B-A55B',
        description: 'NVIDIA — MoE, 550B total / 55B active; ailenin en büyük üyesi',
    },
    {
        value: 'nvidia/llama-3.1-nemotron-70b-instruct',
        label: 'Llama 3.1 Nemotron 70B Instruct',
        description: 'NVIDIA — Llama 3.1 tabanlı, NVIDIA post-training',
    },
    {
        value: 'nvidia/llama-3.1-nemotron-ultra-253b-v1',
        label: 'Llama 3.1 Nemotron Ultra 253B v1',
        description: 'NVIDIA — Llama 3.1 tabanlı dense, uzun-form reasoning',
    },
    {
        value: 'openai/gpt-oss-120b',
        label: 'GPT-OSS 120B',
        description: 'OpenAI open-weight — NIM üzerinde barındırılır',
    },
    {
        value: 'openai/gpt-oss-20b',
        label: 'GPT-OSS 20B',
        description: 'OpenAI open-weight — küçük varyant',
    },
    {
        value: 'mistralai/mistral-large-2-instruct',
        label: 'Mistral Large 2 Instruct',
        description: 'Mistral AI — çok dilli instruct modeli',
    },
    {
        value: 'moonshotai/kimi-k3',
        label: 'Kimi K3',
        description: 'Moonshot AI — agentic kullanım odaklı',
    },
    {
        value: 'minimaxai/minimax-m3',
        label: 'MiniMax M3 (NIM)',
        description: 'MiniMax — NIM üzerinden barındırılan M3',
    },
    {
        value: 'deepseek-ai/deepseek-v4-pro-0813',
        label: 'DeepSeek V4 Pro (0813)',
        description: 'DeepSeek — V4 ailesinin büyük varyantı',
    },
    {
        value: 'deepseek-ai/deepseek-v4-flash-0731',
        label: 'DeepSeek V4 Flash (0731)',
        description: 'DeepSeek — V4 ailesinin hızlı varyantı',
    },
];
/**
 * Model families the NIM catalogue serves that do NOT answer `/chat/completions`
 * in the shape `NimProvider` builds — embedding, guard/safety, reward, translate,
 * parse and CLIP models.
 *
 * SINGLE SOURCE for that judgement. Two consumers read it and they must not
 * disagree: the parity test that keeps `NIM_MODELS` free of these families, and
 * the live-catalogue endpoint, which surfaces uncurated ids straight from
 * NVIDIA and must apply the SAME exclusion before offering one as a chat model.
 * A second hand-written literal in either place is how the two would drift.
 *
 * Deliberately un-anchored and WITHOUT the `g` flag — `g` carries `lastIndex`
 * across calls, so a shared instance would answer differently for the same input
 * depending on what was tested before it.
 */
export const NIM_NON_CHAT_MODEL_PATTERN = /embed|guard|safety|reward|translate|parse|nvclip/;
export const PROVIDER_OPTIONS = [
    { value: 'copilot', label: 'Copilot CLI', description: 'Yerel Copilot CLI üzerinden çalışır (API anahtarı gerektirmez)' },
    { value: 'claude-cli', label: 'Claude CLI', description: 'Local Claude Code CLI subprocess (kişisel görünürlük zorunlu)' },
    { value: 'minimax', label: 'MiniMax API', description: 'MiniMax M2.7/M2.5 modelleri — OpenAI veya Anthropic SDK' },
    { value: 'openai', label: 'OpenAI API', description: 'OpenAI modelleri — doğrudan API erişimi' },
    { value: 'vertex', label: 'Vertex (Gemini)', description: 'Google Gemini modelleri — Express-mode API key (AQ.* format)' },
    { value: 'opencode', label: 'OpenCode', description: 'Yerel OpenCode AI — session geçmişi ve agent yetenekleri' },
    { value: 'custom', label: 'Özel API', description: 'OpenAI uyumlu herhangi bir endpoint' },
    { value: 'anthropic', label: 'Anthropic API', description: 'Anthropic Claude modelleri — doğrudan API erişimi' },
    { value: 'nim', label: 'NVIDIA NIM', description: 'NVIDIA NIM modelleri — hosted (build.nvidia.com API key) veya self-hosted NIM container' },
];
export const PROVIDER_BASE_URLS = {
    minimax: 'https://api.minimax.io/v1',
    'minimax-anthropic': 'https://api.minimax.io/anthropic',
    openai: 'https://api.openai.com/v1',
    vertex: 'https://generativelanguage.googleapis.com/v1beta',
    nim: NIM_HOSTED_BASE_URL,
};
export function getModelsForProvider(provider) {
    switch (provider) {
        case 'copilot': return COPILOT_MODELS;
        case 'minimax': return MINIMAX_MODELS;
        case 'openai': return OPENAI_MODELS;
        case 'vertex': return VERTEX_MODELS;
        case 'opencode': return OPENCODE_MODELS;
        case 'nim': return NIM_MODELS;
        case 'claude-cli': return [];
        case 'custom': return [];
        case 'echo': return [];
        case 'anthropic': return ANTHROPIC_MODELS;
        default: {
            // Exhaustiveness pin: adding a member to AI_PROVIDER_TYPES without a case
            // above fails typecheck here instead of silently falling into the fallback.
            const _exhaustive = provider;
            void _exhaustive;
            // Runtime fallback preserved: a legacy provider string persisted in an old
            // profile row must still render a model list rather than crash the editor.
            return COPILOT_MODELS;
        }
    }
}
//# sourceMappingURL=aiModels.js.map